import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  List,
  ListItemButton,
  ListItemText,
  Typography,
  useTheme,
} from '@mui/material';
import EmailIcon from '@mui/icons-material/Email';
import PageHeader from '../components/common/PageHeader';
import { EmailTemplateOverview, emailTemplatesService } from '../services/emailTemplatesService';

const EmailTemplatesPage: React.FC = () => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const accent = theme.palette.primary.main;
  const bgElevated = isDark ? '#2c3358' : '#f5f7fa';
  const borderColor = theme.palette.divider;

  const [templates, setTemplates] = useState<EmailTemplateOverview[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedId, setSelectedId] = useState<string | null>(null);

  useEffect(() => {
    const loadTemplates = async () => {
      try {
        setLoading(true);
        const data = await emailTemplatesService.list();
        setTemplates(data);
        if (data.length > 0) {
          setSelectedId(data[0].template_id);
        }
      } catch (err: any) {
        const detail = err?.response?.data?.detail || 'Templates konnten nicht geladen werden';
        setError(detail);
      } finally {
        setLoading(false);
      }
    };

    loadTemplates();
  }, []);

  const selected = templates.find((t) => t.template_id === selectedId) || null;

  const getPreviewHtml = (tpl: EmailTemplateOverview): string => {
    let html = tpl.html_template;
    for (const v of tpl.variables) {
      const placeholder = new RegExp(`\\{${v}\\}`, 'g');
      if (v.includes('link')) {
        html = html.replace(placeholder, '#');
      } else {
        html = html.replace(placeholder, `{{${v}}}`);
      }
    }
    return html;
  };

  if (loading) {
    return (
      <Box>
        <PageHeader
          title="E-Mail Templates"
          subtitle="Übersicht aller systemweiten E-Mail-Vorlagen"
          breadcrumbs={[{ label: 'E-Mail Templates' }]}
        />
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      </Box>
    );
  }

  return (
    <Box>
      <PageHeader
        title="E-Mail Templates"
        subtitle="Übersicht aller systemweiten E-Mail-Vorlagen"
        breadcrumbs={[{ label: 'E-Mail Templates' }]}
      />

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Box sx={{ display: 'flex', gap: 2, minHeight: 600 }}>
        {/* Left panel: template list */}
        <Card sx={{ width: 300, flexShrink: 0 }}>
          <CardContent sx={{ p: 0 }}>
            <Box sx={{ px: 2, py: 1.5, borderBottom: `1px solid ${borderColor}` }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, color: accent, textTransform: 'uppercase', fontSize: '0.7rem', letterSpacing: '0.05em' }}>
                Templates ({templates.length})
              </Typography>
            </Box>
            <List disablePadding>
              {templates.map((tpl) => (
                <ListItemButton
                  key={tpl.template_id}
                  selected={selectedId === tpl.template_id}
                  onClick={() => setSelectedId(tpl.template_id)}
                  sx={{
                    borderLeft: selectedId === tpl.template_id ? `3px solid ${accent}` : '3px solid transparent',
                    py: 1.5,
                    '&.Mui-selected': {
                      bgcolor: bgElevated,
                    },
                  }}
                >
                  <EmailIcon sx={{ mr: 1.5, fontSize: 20, color: selectedId === tpl.template_id ? accent : 'text.secondary' }} />
                  <ListItemText
                    primary={tpl.name}
                    secondary={tpl.template_id}
                    primaryTypographyProps={{ fontSize: '0.875rem', fontWeight: selectedId === tpl.template_id ? 600 : 400 }}
                    secondaryTypographyProps={{ fontSize: '0.7rem', fontFamily: 'monospace' }}
                  />
                </ListItemButton>
              ))}
            </List>
          </CardContent>
        </Card>

        {/* Right panel: detail + preview */}
        <Card sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
          <CardContent sx={{ flex: 1, display: 'flex', flexDirection: 'column', p: 0 }}>
            {selected ? (
              <>
                {/* Metadata header */}
                <Box sx={{ px: 3, py: 2, borderBottom: `1px solid ${borderColor}` }}>
                  <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5 }}>
                    {selected.name}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                    {selected.description}
                  </Typography>

                  <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap', mb: 1 }}>
                    <Box>
                      <Typography variant="caption" sx={{ fontWeight: 700, textTransform: 'uppercase', color: 'text.secondary', letterSpacing: '0.05em' }}>
                        Template-ID
                      </Typography>
                      <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                        {selected.template_id}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" sx={{ fontWeight: 700, textTransform: 'uppercase', color: 'text.secondary', letterSpacing: '0.05em' }}>
                        Betreff
                      </Typography>
                      <Typography variant="body2">
                        {selected.subject_preview}
                      </Typography>
                    </Box>
                  </Box>

                  <Box>
                    <Typography variant="caption" sx={{ fontWeight: 700, textTransform: 'uppercase', color: 'text.secondary', letterSpacing: '0.05em', display: 'block', mb: 0.5 }}>
                      Variablen
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                      {selected.variables.map((v) => (
                        <Chip
                          key={v}
                          label={`{${v}}`}
                          size="small"
                          sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}
                        />
                      ))}
                    </Box>
                  </Box>
                </Box>

                {/* HTML Preview */}
                <Box sx={{ flex: 1, p: 3, bgcolor: isDark ? '#1a1f3c' : '#fafbfc', overflow: 'auto' }}>
                  <Typography variant="caption" sx={{ fontWeight: 700, textTransform: 'uppercase', color: 'text.secondary', letterSpacing: '0.05em', display: 'block', mb: 1 }}>
                    Vorschau
                  </Typography>
                  <Box
                    sx={{
                      border: `1px solid ${borderColor}`,
                      borderRadius: 2,
                      overflow: 'hidden',
                      bgcolor: '#ffffff',
                    }}
                  >
                    <iframe
                      srcDoc={getPreviewHtml(selected)}
                      title={`Vorschau: ${selected.name}`}
                      style={{
                        width: '100%',
                        minHeight: 400,
                        border: 'none',
                        display: 'block',
                      }}
                      sandbox=""
                    />
                  </Box>
                </Box>
              </>
            ) : (
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flex: 1, color: 'text.secondary' }}>
                <Typography>Kein Template ausgewaehlt</Typography>
              </Box>
            )}
          </CardContent>
        </Card>
      </Box>
    </Box>
  );
};

export default EmailTemplatesPage;
